import {
  CDN_ASSETS,
  isCaponeTenant,
  useGetAllWishlistsQuery,
  useUpdateWishlistMutation,
  wishlistsApi,
} from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import {
  B2BLoadingPopup,
  Header,
  HotelSplitMapView,
  pluralize,
  useDeviceTypes,
  WishlistHeaderEntry,
  wishlistManagementModalActions,
} from "halifax";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { RouterProps, useLocation } from "react-router";

import clsx from "clsx";

import { ClientContext } from "../../App";
import { PATH_HOME, PATH_HOME_STAYS } from "../../utils/paths";

import {
  useGetAllWishlistItemsByUserQuery,
  useGetWishlistByIdQuery,
} from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { config } from "../../api/config";
import {
  AVAILABLE,
  getExperimentVariant,
  STAYS_SEARCH,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import {
  AvailabilityErrorModal,
  AvailabilityList,
} from "../availability/components";
import { ConnectedAvailabilityClusterMap } from "../availability/components/AvailabilityClusterMap/container";
import { HotelAvailabilityCallState } from "../availability/reducer/state";
import "../availability/styles.scss";
import * as constants from "../availability/textConstants";
import { RewardsAccountSelection } from "../rewards/components";
import { TravelWalletDrawer } from "../travel-wallet/components";
import { ConnectedMobileWishlistAvailability } from "./components/MobileWishlistAvailability/container";
import { ConnectedWishlistManagementModal } from "./components/WishlistManagementModal/container";
import { WishlistSnackbar } from "./components/WishlistSnackbar/component";
import { WishlistAvailabilityConnectorProps } from "./container";
import styles from "./styles.module.scss";

export interface IWishlistAvailability
  extends WishlistAvailabilityConnectorProps,
    RouterProps {}

export const WishlistAvailability = (props: IWishlistAvailability) => {
  const {
    hotelAvailabilityCallState,
    fetchWishlistAvailability,
    history,
    fetchTravelWalletDetails,
    largestValueAccount,
    fetchTravelWalletCreditHistory,
    listPaymentMethods,
  } = props;

  const { logo } = useContext(ClientContext);
  const { matchesMobile } = useDeviceTypes();
  const location = useLocation();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);

  // If search term is location (ex: Toronto) vs if search term is point of interest (ex: Toronto Zoo)
  const [mobileMapOpen, setMobileMapOpen] = useState(false);
  const [wishlistId, setWishlistId] = useState<string | undefined>(undefined);

  const expState = useExperiments();

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );

  const staysSearchEnabled =
    getExperimentVariant(expState.experiments, STAYS_SEARCH) === AVAILABLE;

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const hasWishlists = Boolean(
    useSelector(wishlistsApi.endpoints.getAllWishlists.select())?.data
      ?.wishlists?.length
  );

  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const showEarnEnhancement =
    isCaponeTenant(config.TENANT) &&
    !!largestValueAccount &&
    !!largestValueAccount.earn.hotelsMultiplier;

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(() => {
    return travelCreditHistoryExperiment === AVAILABLE;
  }, [travelCreditHistoryExperiment]);

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  useEffect(() => {
    fetchTravelWalletDetails();
    listPaymentMethods();
  }, []);

  useEffect(() => {
    const id = searchParams.get("id");

    if (id) {
      setWishlistId(id);
    }
  }, [searchParams]);

  useGetAllWishlistItemsByUserQuery();
  useGetAllWishlistsQuery();
  const [updateWishlist] = useUpdateWishlistMutation();

  const { data: { wishlist } = {} } = useGetWishlistByIdQuery(
    { wishlistId: { value: wishlistId ?? "" } },
    {
      skip: !wishlistId, // Skip until wishlistId is parsed from the URL
    }
  );

  useEffect(() => {
    if (wishlist) {
      const { checkInDate, checkOutDate } = wishlist.wishlistData;
      const today = dayjs().startOf("day");
      if (
        (checkInDate && dayjs(checkInDate).isBefore(today)) ||
        (checkOutDate && dayjs(checkOutDate).isBefore(today))
      ) {
        // If the wishlist stay dates are in the past, update them valid future dates. This will clear the RTK cache and cause a re-render.
        updateWishlist({
          wishlistId: wishlist?.id,
          wishlistData: {
            name: wishlist?.wishlistData?.name,
            adults: wishlist?.wishlistData?.adults,
            children: wishlist?.wishlistData?.children,
            childrenAges: wishlist?.wishlistData?.childrenAges,
            petsIncluded: wishlist?.wishlistData?.petsIncluded,
            checkInDate: today.add(2, "day").format("YYYY-MM-DD"),
            checkOutDate: today.add(2 + 4, "day").format("YYYY-MM-DD"),
          },
        });
      } else {
        fetchWishlistAvailability({
          history,
          lodgingIds: wishlist.items.map((item) => item.product.id.value),
          stayDates: {
            fromDate: wishlist.wishlistData?.checkInDate || dayjs().toDate(),
            untilDate:
              wishlist.wishlistData?.checkOutDate ||
              dayjs().add(7, "day").toDate(),
          },
          guests: {
            numAdults: wishlist.wishlistData?.adults || 2,
            numChildren: wishlist.wishlistData?.children || 0,
            childrenAges: wishlist.wishlistData?.childrenAges || [],
            petsIncluded: wishlist.wishlistData?.petsIncluded || false,
          },
          numRooms: wishlist.wishlistData?.roomCount || 1,
        });
      }
    }
  }, [wishlist]);

  useEffect(() => {
    if (isTravelCreditHistoryExperiment) {
      fetchTravelWalletCreditHistory();
    }
  }, [isTravelCreditHistoryExperiment]);

  const renderDesktopView = () => (
    <HotelSplitMapView
      className={clsx("hotel-availability-container", config.TENANT)}
      header={
        <Header
          className="rewards-components-section"
          left={
            <Box className={"rewards-account-section-left-content"}>
              <Box
                className={"logo"}
                onClick={() =>
                  history.push(staysSearchEnabled ? PATH_HOME_STAYS : PATH_HOME)
                }
              >
                {logo}
              </Box>
              <Box className={"rewards-account-section-travel-details"}>
                <Typography variant={"body1"} tabIndex={0}>
                  {wishlist?.wishlistData?.name}
                </Typography>
                {wishlist?.items && wishlist?.items?.length > 0 && (
                  <Typography variant={"body2"}>
                    ({wishlist.items.length}){" "}
                    {pluralize(wishlist.items.length, "stay", "stays")} saved
                  </Typography>
                )}
              </Box>
            </Box>
          }
          right={
            <Box className="desktop-hotel-availability-rewards-account-contents">
              <RewardsAccountSelection
                className={clsx("b2b", {
                  "hide-balance-border": isTravelWalletCreditsExperiment,
                })}
                popoverClassName="b2b"
              />

              {isWishlistExperiment && (
                <WishlistHeaderEntry
                  hasWishlists={hasWishlists}
                  onClick={() =>
                    dispatch(wishlistManagementModalActions.openModal({}))
                  }
                />
              )}

              {isTravelWalletCreditsExperiment && <TravelWalletDrawer />}
            </Box>
          }
        />
      }
      leftClassName={clsx(
        "list-section",
        hotelAvailabilityCallState ===
          HotelAvailabilityCallState.InitialSearchCallInProcess &&
          styles.wishlistListSectionOverrides
      )}
      left={<AvailabilityList isWishlistMode={true} />}
      rightClassName={clsx("map-section")}
      right={<ConnectedAvailabilityClusterMap isWishlistMode />}
    />
  );

  const renderMobileView = () => (
    <Box
      className={clsx("hotel-availability-container", config.TENANT, {
        "mobile-map": mobileMapOpen,
      })}
    >
      {wishlist && (
        <ConnectedMobileWishlistAvailability
          onShowMap={setMobileMapOpen}
          wishlist={wishlist}
        />
      )}
    </Box>
  );

  const capOneLoadingSecondaryText = showEarnEnhancement
    ? constants.EARN_ENHANCEMENT_SUBTITLE(
        largestValueAccount.earn.hotelsMultiplier,
        staysSearchEnabled ? largestValueAccount.earn.homesMultiplier : "",
        largestValueAccount.productDisplayName,
        largestValueAccount.rewardsBalance.currencyDescription ??
          largestValueAccount.rewardsBalance.currency
      )
    : largestValueAccount?.productDisplayName.toLowerCase().includes("paradise")
    ? constants.PARADISE_SUBTITLE
    : constants.fetchingAvailabilitySecondaryText(staysSearchEnabled);

  return (
    <>
      {hotelAvailabilityCallState ===
        HotelAvailabilityCallState.InitialSearchCallInProcess ||
      (matchesMobile &&
        hotelAvailabilityCallState ==
          HotelAvailabilityCallState.InitialMapSearchCallInProcess) ? (
        <B2BLoadingPopup
          open={
            hotelAvailabilityCallState ===
              HotelAvailabilityCallState.InitialSearchCallInProcess ||
            (matchesMobile &&
              hotelAvailabilityCallState ==
                HotelAvailabilityCallState.InitialMapSearchCallInProcess)
          }
          message={""}
          secondaryMessage={capOneLoadingSecondaryText}
          image={CDN_ASSETS.capone.searchLoading.hotels}
          className="hotel-search-loading-popup"
          popupSize={matchesMobile ? "mobile" : "desktop"}
          isXmod={isXmodRedesign}
        />
      ) : null}
      <Box
        className={clsx("hotel-availability-root", {
          "full-width": !matchesMobile,
        })}
      >
        {matchesMobile ? renderMobileView() : renderDesktopView()}
      </Box>

      <ConnectedWishlistManagementModal
        history={history}
        entryType="favorites_availability"
      />
      <WishlistSnackbar />
      <AvailabilityErrorModal />
    </>
  );
};
